import { IntegrationType } from '@accedo/one-plugin-sdk';

import { mergeAnalyticsFeatureWithIntegrations } from '#/plugins/analytics/analyticsUtils';
import { pluginManager } from '#/plugins/pluginManager';
import { MainConfig } from '#/service/config/config.types';
import { FeatureConfig } from '#/service/config/features.types';

// keys of features that are now configurable in `integrations`, but can still be configured in `features`
type LegacyFeatureKey =
  | 'analytics'
  | 'paywall'
  | 'privacy'
  | 'pageAds'
  | 'videoPlayer'
  | 'ads';

// mapping new `integration` types back to the old `features` keys.
// for example the `PAYWALL` integration type used to be configured in `features.paywall` when it was used as a plugin.
const integrationTypeToFeatureKey: Partial<
  Record<IntegrationType, LegacyFeatureKey>
> = {
  [IntegrationType.CLIENT_ANALYTICS]: 'analytics',
  [IntegrationType.PAYWALL]: 'paywall',
  [IntegrationType.CONSENT]: 'privacy',
  [IntegrationType.PAGE_ADS]: 'pageAds',
  [IntegrationType.VIDEO_PLAYER]: 'videoPlayer',
  [IntegrationType.VIDEO_ADS]: 'ads',
};

const getLegacyConfig = (type: IntegrationType, config: MainConfig) => {
  if (type === IntegrationType.CLIENT_ANALYTICS) {
    return mergeAnalyticsFeatureWithIntegrations(config);
  }

  const featureKey = integrationTypeToFeatureKey[type];

  return featureKey && config.features?.[featureKey];
};

export const getPluginConfigs = (config: MainConfig, type: IntegrationType) => {
  const integrations: FeatureConfig<any>[] = [];
  const loadedPlugins = pluginManager.getLoadedPlugins();

  if (!config.integrationsVersionOverrides?.properties?.disableIntegrations) {
    Object.entries(config.integrations).forEach(([id, integration]) => {
      const didPluginLoad = loadedPlugins[`${type}--${id}`];

      if (integration.activeForFeatures.includes(type) && didPluginLoad) {
        integrations.push({
          enabled: true,
          key: id,
          properties: {
            ...integration.configuration,
          },
        });
      }
    });
  }

  const legacyConfig = getLegacyConfig(type, config);

  legacyConfig?.forEach?.((legacyFeature: FeatureConfig<any>) => {
    if (!legacyFeature.enabled) {
      return;
    }

    const exists = integrations.find(
      ({ properties }) => properties.pluginId === legacyFeature.plugin?.id
    );

    if (!exists) {
      integrations.push({
        enabled: true,
        key: legacyFeature.plugin?.id ?? '',
        properties: legacyFeature.properties,
        plugin: legacyFeature.plugin,
      });
    }
  });

  return {
    first: integrations[0],
    all: integrations,
  };
};
